/**
 * WORLD office interior at x > 30 — shell with door at (30, 0, 0), frosted
 * pane, window with venetian blind GEOMETRY (LIGHT casts the actual slatted
 * light), desk, chair, filing cabinet, and a slowly rotating ceiling fan.
 */
import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  type Object3D
} from 'three'
import { OFFICE_DOOR_POSITION } from '../core/constants'
import {
  BLIND_SLAT_COUNT,
  BLIND_SLAT_TILT,
  COLOR_DARK_METAL,
  DESK_HEIGHT,
  OFFICE_CEILING_HEIGHT,
  OFFICE_DOOR_AJAR_ANGLE,
  OFFICE_DOOR_HALF_WIDTH,
  OFFICE_DOOR_HEIGHT,
  OFFICE_X_MAX,
  OFFICE_X_MIN,
  OFFICE_Z_HALF,
  WINDOW_Y_MAX,
  WINDOW_Y_MIN,
  WINDOW_Z_HALF
} from './constants'
import { enableShadows, interactRadius, tagInteract, tagSolid } from './helpers'
import {
  createFrostedGlassTexture,
  createPlasterTexture,
  createWoodTexture
} from './textures'

export interface OfficeBuild {
  readonly group: Group
  /** Ceiling fan rotor; index.ts spins it on the game loop. */
  readonly fanRotor: Object3D
}

const WALL_THICKNESS = 0.25
const ROOM_WIDTH = OFFICE_X_MAX - OFFICE_X_MIN
const ROOM_CENTER_X = (OFFICE_X_MIN + OFFICE_X_MAX) / 2
const DOOR_LABEL = 'GUMSHOE\nINVESTIGATIONS'
const DESK_POSITION = { x: 37.6, z: -0.6 } as const
/** Matches LIGHT's desk-lamp spot at (37.3, 1.18, -0.6). */
const DESK_LAMP_POSITION = { x: 37.3, z: -0.6 } as const
const CHAIR_POSITION = { x: 38.8, z: -0.6 } as const
const CABINET_POSITION = { x: 39.5, z: 4.4 } as const
const FAN_POSITION = { x: 34.8, z: 0 } as const

const addWallBox = (
  group: Group,
  material: MeshStandardMaterial,
  size: readonly [number, number, number],
  position: readonly [number, number, number]
): void => {
  const wall = new Mesh(new BoxGeometry(...size), material)
  wall.position.set(...position)
  enableShadows(wall)
  tagSolid(wall)
  group.add(wall)
}

const buildShell = (group: Group): void => {
  const plaster = createPlasterTexture()
  plaster.repeat.set(4, 2)
  const wallMaterial = new MeshStandardMaterial({ map: plaster, roughness: 0.9 })

  const floorTexture = createWoodTexture()
  floorTexture.repeat.set(4, 4)
  const floor = new Mesh(
    new BoxGeometry(ROOM_WIDTH, 0.1, OFFICE_Z_HALF * 2),
    new MeshStandardMaterial({ map: floorTexture, roughness: 0.55, metalness: 0.1 })
  )
  floor.position.set(ROOM_CENTER_X, -0.05, 0)
  floor.receiveShadow = true
  tagSolid(floor)
  group.add(floor)

  const ceiling = new Mesh(
    new BoxGeometry(ROOM_WIDTH, 0.1, OFFICE_Z_HALF * 2),
    new MeshStandardMaterial({ color: 0x16140f, roughness: 0.98 })
  )
  ceiling.position.set(ROOM_CENTER_X, OFFICE_CEILING_HEIGHT + 0.05, 0)
  tagSolid(ceiling)
  group.add(ceiling)

  // North / south walls.
  const sides: readonly number[] = [-1, 1]
  sides.forEach((side) => {
    addWallBox(
      group,
      wallMaterial,
      [ROOM_WIDTH, OFFICE_CEILING_HEIGHT, WALL_THICKNESS],
      [ROOM_CENTER_X, OFFICE_CEILING_HEIGHT / 2, side * (OFFICE_Z_HALF + WALL_THICKNESS / 2)]
    )
  })

  // West wall (street side) with the door opening at z in [-0.65, 0.65].
  const westX = OFFICE_X_MIN - WALL_THICKNESS / 2
  const flankLength = OFFICE_Z_HALF - OFFICE_DOOR_HALF_WIDTH
  sides.forEach((side) => {
    addWallBox(
      group,
      wallMaterial,
      [WALL_THICKNESS, OFFICE_CEILING_HEIGHT, flankLength],
      [westX, OFFICE_CEILING_HEIGHT / 2, side * (OFFICE_DOOR_HALF_WIDTH + flankLength / 2)]
    )
  })
  const lintelHeight = OFFICE_CEILING_HEIGHT - OFFICE_DOOR_HEIGHT
  addWallBox(
    group,
    wallMaterial,
    [WALL_THICKNESS, lintelHeight, OFFICE_DOOR_HALF_WIDTH * 2],
    [westX, OFFICE_DOOR_HEIGHT + lintelHeight / 2, 0]
  )

  // East wall with the window opening.
  const eastX = OFFICE_X_MAX + WALL_THICKNESS / 2
  const windowFlank = OFFICE_Z_HALF - WINDOW_Z_HALF
  sides.forEach((side) => {
    addWallBox(
      group,
      wallMaterial,
      [WALL_THICKNESS, OFFICE_CEILING_HEIGHT, windowFlank],
      [eastX, OFFICE_CEILING_HEIGHT / 2, side * (WINDOW_Z_HALF + windowFlank / 2)]
    )
  })
  addWallBox(
    group,
    wallMaterial,
    [WALL_THICKNESS, WINDOW_Y_MIN, WINDOW_Z_HALF * 2],
    [eastX, WINDOW_Y_MIN / 2, 0]
  )
  const headerHeight = OFFICE_CEILING_HEIGHT - WINDOW_Y_MAX
  addWallBox(
    group,
    wallMaterial,
    [WALL_THICKNESS, headerHeight, WINDOW_Z_HALF * 2],
    [eastX, WINDOW_Y_MAX + headerHeight / 2, 0]
  )
}

const buildWindow = (group: Group): void => {
  const windowHeight = WINDOW_Y_MAX - WINDOW_Y_MIN
  const glass = new Mesh(
    new BoxGeometry(0.02, windowHeight, WINDOW_Z_HALF * 2),
    new MeshStandardMaterial({
      color: 0x9db4c4,
      roughness: 0.05,
      metalness: 0.2,
      transparent: true,
      opacity: 0.15
    })
  )
  glass.position.set(OFFICE_X_MAX, WINDOW_Y_MIN + windowHeight / 2, 0)
  group.add(glass)

  // Venetian blinds: tilted slats just inside the glass, shadow-casting so
  // LIGHT's window source stripes the room. Near-black so the slats read as
  // hard silhouettes between the light blades, not lit white bars
  // (QA round 2: bright slats read as solid 3D geometry).
  const slatMaterial = new MeshStandardMaterial({ color: 0x232119, roughness: 0.85 })
  const slatGap = windowHeight / BLIND_SLAT_COUNT
  const blinds = new Group()
  blinds.name = 'venetian-blinds'
  for (let i = 0; i < BLIND_SLAT_COUNT; i += 1) {
    const slat = new Mesh(new BoxGeometry(0.09, 0.018, WINDOW_Z_HALF * 2 + 0.2), slatMaterial)
    slat.rotation.z = BLIND_SLAT_TILT
    slat.position.set(OFFICE_X_MAX - 0.16, WINDOW_Y_MIN + slatGap * (i + 0.5), 0)
    slat.castShadow = true
    blinds.add(slat)
  }
  group.add(blinds)
}

const buildDoor = (group: Group): void => {
  const hinge = new Group()
  hinge.name = 'office-door'
  hinge.position.set(OFFICE_DOOR_POSITION.x, OFFICE_DOOR_POSITION.y, OFFICE_DOOR_HALF_WIDTH)

  const woodMaterial = new MeshStandardMaterial({
    map: createWoodTexture(),
    roughness: 0.6,
    metalness: 0.05
  })
  const panel = new Mesh(
    new BoxGeometry(0.06, OFFICE_DOOR_HEIGHT, OFFICE_DOOR_HALF_WIDTH * 2),
    woodMaterial
  )
  panel.position.set(0, OFFICE_DOOR_HEIGHT / 2, -OFFICE_DOOR_HALF_WIDTH)
  enableShadows(panel)
  tagSolid(panel)
  tagInteract(panel, {
    id: 'door-office',
    kind: 'door',
    prompt: 'Your office. The door hangs open.',
    radius: interactRadius()
  })
  hinge.add(panel)

  const paneHeight = 0.9
  const pane = new Mesh(
    new BoxGeometry(0.02, paneHeight, OFFICE_DOOR_HALF_WIDTH * 2 - 0.3),
    new MeshStandardMaterial({
      map: createFrostedGlassTexture(DOOR_LABEL),
      roughness: 0.85,
      transparent: true,
      opacity: 0.6
    })
  )
  pane.position.set(0, OFFICE_DOOR_HEIGHT - paneHeight / 2 - 0.25, -OFFICE_DOOR_HALF_WIDTH)
  hinge.add(pane)

  hinge.rotation.y = OFFICE_DOOR_AJAR_ANGLE
  group.add(hinge)
}

const buildDesk = (group: Group): void => {
  const wood = new MeshStandardMaterial({
    map: createWoodTexture(),
    roughness: 0.45,
    metalness: 0.05
  })
  const desk = new Group()
  desk.name = 'desk'
  const top = new Mesh(new BoxGeometry(1.0, 0.06, 1.9), wood)
  top.position.y = DESK_HEIGHT
  enableShadows(top)
  tagSolid(top)
  desk.add(top)
  const legOffsets: ReadonlyArray<readonly [number, number]> = [
    [-0.42, -0.85],
    [-0.42, 0.85],
    [0.42, -0.85],
    [0.42, 0.85]
  ]
  legOffsets.forEach(([lx, lz]) => {
    const leg = new Mesh(new BoxGeometry(0.08, DESK_HEIGHT, 0.08), wood)
    leg.position.set(lx, DESK_HEIGHT / 2, lz)
    enableShadows(leg)
    tagSolid(leg)
    desk.add(leg)
  })
  desk.position.set(DESK_POSITION.x, 0, DESK_POSITION.z)
  group.add(desk)

  // Desk lamp — shade cone around LIGHT's warm spot at (37.3, 1.18, -0.6)
  // so the practical reads as a lamp, not a floating blob of light.
  const lamp = new Group()
  lamp.name = 'desk-lamp'
  const lampMetal = new MeshStandardMaterial({
    color: 0x1f2a1f,
    roughness: 0.45,
    metalness: 0.6
  })
  const lampBase = new Mesh(new CylinderGeometry(0.09, 0.11, 0.03, 12), lampMetal)
  lampBase.position.y = 0.015
  lamp.add(lampBase)
  const lampStem = new Mesh(new CylinderGeometry(0.015, 0.015, 0.42, 8), lampMetal)
  lampStem.position.y = 0.23
  lamp.add(lampStem)
  const lampShade = new Mesh(new CylinderGeometry(0.07, 0.16, 0.14, 14, 1, true), lampMetal)
  lampShade.position.y = 0.42
  lamp.add(lampShade)
  const lampBulb = new Mesh(
    new CylinderGeometry(0.05, 0.05, 0.02, 10),
    new MeshStandardMaterial({
      color: 0x000000,
      emissive: 0xffd9a0,
      // Below the bloom threshold so the shade and desk stay legible
      // instead of clipping to a white blob (QA round 2).
      emissiveIntensity: 0.85,
      roughness: 0.6
    })
  )
  lampBulb.position.y = 0.38
  lamp.add(lampBulb)
  lamp.position.set(DESK_LAMP_POSITION.x, DESK_HEIGHT + 0.03, DESK_LAMP_POSITION.z)
  group.add(lamp)

  // Desk dressing — a small readable island for the lamp light to fall ON:
  // scattered case papers, an ashtray, a whiskey glass.
  const dressing = new Group()
  dressing.name = 'desk-dressing'
  const paperMaterial = new MeshStandardMaterial({ color: 0xcfc6ad, roughness: 0.9 })
  const paperSpecs: ReadonlyArray<readonly [number, number, number]> = [
    // [xOffset, zOffset, yawRad]
    [0.18, 0.25, 0.3],
    [0.22, 0.05, -0.18],
    [-0.05, 0.45, 0.65]
  ]
  paperSpecs.forEach(([px, pz, yaw]) => {
    const paper = new Mesh(new BoxGeometry(0.21, 0.004, 0.3), paperMaterial)
    paper.rotation.y = yaw
    paper.position.set(DESK_POSITION.x + px, DESK_HEIGHT + 0.035, DESK_POSITION.z + pz)
    paper.receiveShadow = true
    dressing.add(paper)
  })
  const ashtray = new Mesh(
    new CylinderGeometry(0.07, 0.05, 0.035, 12),
    new MeshStandardMaterial({ color: 0x2e3236, roughness: 0.3, metalness: 0.5 })
  )
  ashtray.position.set(DESK_POSITION.x - 0.25, DESK_HEIGHT + 0.05, DESK_POSITION.z + 0.55)
  enableShadows(ashtray)
  dressing.add(ashtray)
  const whiskeyGlass = new Mesh(
    new CylinderGeometry(0.035, 0.03, 0.09, 10),
    new MeshStandardMaterial({
      color: 0x8a6a30,
      roughness: 0.05,
      metalness: 0.1,
      transparent: true,
      opacity: 0.55
    })
  )
  whiskeyGlass.position.set(DESK_POSITION.x - 0.3, DESK_HEIGHT + 0.078, DESK_POSITION.z - 0.4)
  enableShadows(whiskeyGlass)
  dressing.add(whiskeyGlass)
  group.add(dressing)

  const chair = new Group()
  chair.name = 'chair'
  const seat = new Mesh(new BoxGeometry(0.46, 0.06, 0.46), wood)
  seat.position.y = 0.48
  enableShadows(seat)
  tagSolid(seat)
  chair.add(seat)
  const back = new Mesh(new BoxGeometry(0.06, 0.55, 0.46), wood)
  back.position.set(0.23, 0.78, 0)
  enableShadows(back)
  tagSolid(back)
  chair.add(back)
  legOffsets.forEach(([lx, lz]) => {
    const leg = new Mesh(new BoxGeometry(0.05, 0.48, 0.05), wood)
    leg.position.set(lx * 0.45, 0.24, lz * 0.24)
    chair.add(leg)
  })
  chair.position.set(CHAIR_POSITION.x, 0, CHAIR_POSITION.z)
  group.add(chair)
}

const buildFilingCabinet = (group: Group): void => {
  const metal = new MeshStandardMaterial({
    color: COLOR_DARK_METAL,
    roughness: 0.5,
    metalness: 0.7
  })
  const cabinet = new Group()
  cabinet.name = 'filing-cabinet'
  const body = new Mesh(new BoxGeometry(0.55, 1.3, 0.6), metal)
  body.position.y = 0.65
  enableShadows(body)
  tagSolid(body)
  cabinet.add(body)
  const drawerCount = 3
  for (let i = 0; i < drawerCount; i += 1) {
    const handle = new Mesh(new BoxGeometry(0.16, 0.03, 0.03), metal)
    handle.position.set(0, 0.3 + i * 0.4, 0.32)
    cabinet.add(handle)
  }
  cabinet.rotation.y = Math.PI
  cabinet.position.set(CABINET_POSITION.x, 0, CABINET_POSITION.z)
  group.add(cabinet)
}

const buildCeilingFan = (group: Group): Object3D => {
  const metal = new MeshStandardMaterial({
    color: COLOR_DARK_METAL,
    roughness: 0.4,
    metalness: 0.8
  })
  const fan = new Group()
  fan.name = 'ceiling-fan'
  const rod = new Mesh(new CylinderGeometry(0.025, 0.025, 0.35, 8), metal)
  rod.position.y = -0.175
  fan.add(rod)

  const rotor = new Group()
  rotor.name = 'ceiling-fan-rotor'
  rotor.position.y = -0.38
  const hub = new Mesh(new CylinderGeometry(0.1, 0.12, 0.1, 10), metal)
  rotor.add(hub)
  const bladeCount = 4
  const bladeMaterial = new MeshStandardMaterial({ color: 0x221a12, roughness: 0.85 })
  for (let i = 0; i < bladeCount; i += 1) {
    const blade = new Mesh(new BoxGeometry(1.0, 0.02, 0.18), bladeMaterial)
    const angle = (i / bladeCount) * Math.PI * 2
    blade.position.set(Math.cos(angle) * 0.6, 0, Math.sin(angle) * 0.6)
    blade.rotation.y = -angle
    blade.castShadow = true
    rotor.add(blade)
  }
  fan.add(rotor)
  fan.position.set(FAN_POSITION.x, OFFICE_CEILING_HEIGHT, FAN_POSITION.z)
  group.add(fan)
  return rotor
}

export const buildOffice = (): OfficeBuild => {
  const group = new Group()
  group.name = 'office'
  buildShell(group)
  buildWindow(group)
  buildDoor(group)
  buildDesk(group)
  buildFilingCabinet(group)
  const fanRotor = buildCeilingFan(group)
  return { group, fanRotor }
}
